import React, { useState } from 'react';
import { Loader2, Building2, X, Database, KeyRound, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

import { adminApi } from '../../services/adminApi';

/**
 * Super-admin quick-provision modal.
 *
 * Bare-minimum: Company name + Admin name + Admin email + Plan.
 * Optional: Allocate SQL pre-config so the customer can run their first import
 * on first sign-in without any setup. All Allocate fields are completely
 * optional — leave them blank and the admin sets them up themselves later.
 *
 * Response includes the auto-generated `initial_password` ONCE — we render a
 * success-state with a copyable credentials card so the super-admin can share it.
 */
const AdminCreateTenantModal = ({ onClose, onCreated }) => {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);   // success state — credentials card
  const [showAllocate, setShowAllocate] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    admin_name: '',
    admin_email: '',
    plan: 'professional',
    office_user_seats: '',
    allocate_sql_default_server: '',
    allocate_sql_default_database: '',
    allocate_sql_username: '',
    allocate_sql_password: '',
    allocate_sql_port: 1433,
    send_welcome_email: true,
    enable_allocate_integration: true,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;

    if (!form.company_name.trim() || !form.admin_name.trim() || !form.admin_email.trim()) {
      toast.error('Company name, admin name and email are all required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        admin_name: form.admin_name.trim(),
        admin_email: form.admin_email.trim().toLowerCase(),
        plan: form.plan,
        send_welcome_email: form.send_welcome_email,
        enable_allocate_integration: form.enable_allocate_integration,
      };
      if (form.plan === 'professional' && form.office_user_seats !== '') {
        payload.office_user_seats = parseInt(form.office_user_seats, 10);
      }
      // Only send Allocate fields if any of them are populated
      if (form.allocate_sql_default_server.trim()) {
        payload.allocate_sql_default_server = form.allocate_sql_default_server.trim();
      }
      if (form.allocate_sql_default_database.trim()) {
        payload.allocate_sql_default_database = form.allocate_sql_default_database.trim();
      }
      if (form.allocate_sql_username.trim() && form.allocate_sql_password.trim()) {
        payload.allocate_sql_username = form.allocate_sql_username.trim();
        payload.allocate_sql_password = form.allocate_sql_password;
        payload.allocate_sql_port = parseInt(form.allocate_sql_port, 10) || 1433;
      }

      const { data } = await adminApi.createTenant(payload);
      setResult(data);
      toast.success(`Tenant ${data.client_id} created`);
      // Push a minimal row into the table immediately for the parent's optimistic UI.
      onCreated?.({
        id: data.tenant_id,
        client_id: data.client_id,
        name: data.company_name,
        plan: data.plan,
        office_user_seats: payload.office_user_seats,
        source: 'super_admin_create',
        country: null,
        fleet_size: null,
        asset_count: 0,
        user_count: 1,
        last_active_at: null,
        created_at: new Date().toISOString(),
      });
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (typeof msg === 'string') {
        toast.error(msg);
      } else if (err.response?.status === 409) {
        toast.error('An account with this email already exists.');
      } else {
        toast.error('Could not create tenant');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (label, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
      data-testid="admin-create-tenant-modal"
    >
      <div
        className="bg-[#1a1a1a] border border-[#FFD108]/30 rounded-xl max-w-xl w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FFD108]/15 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-[#FFD108]" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-white">
                {result ? 'Tenant ready ✓' : 'Create new tenant'}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {result
                  ? `${result.company_name} · ${result.client_id}`
                  : 'Bare minimum setup — the admin can configure the rest after first sign-in.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-muted-foreground hover:text-white p-1 rounded"
            data-testid="admin-create-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {result ? (
          <SuccessCard result={result} copy={copy} onClose={onClose} />
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="admin-create-tenant-form">
            <Field label="Company name *" testid="create-company-name">
              <Input
                value={form.company_name}
                onChange={(v) => set('company_name', v)}
                placeholder="Acme Cranes Pty Ltd"
                autoFocus
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Admin name *" testid="create-admin-name">
                <Input
                  value={form.admin_name}
                  onChange={(v) => set('admin_name', v)}
                  placeholder="Anna Operator"
                />
              </Field>
              <Field label="Admin email *" testid="create-admin-email">
                <Input
                  type="email"
                  value={form.admin_email}
                  onChange={(v) => set('admin_email', v)}
                  placeholder="anna@acme.com.au"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Plan" testid="create-plan">
                <div className="flex gap-1 bg-[#0e0e0e] rounded-lg p-1 border border-white/5">
                  {[
                    { v: 'free', l: 'Free' },
                    { v: 'professional', l: 'Pro' },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => set('plan', opt.v)}
                      className={`flex-1 h-9 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors ${
                        form.plan === opt.v
                          ? 'bg-[#FFD108] text-black'
                          : 'text-muted-foreground hover:text-white'
                      }`}
                      data-testid={`create-plan-${opt.v}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </Field>
              {form.plan === 'professional' && (
                <Field label="Office user seats" testid="create-seats">
                  <Input
                    type="number"
                    value={form.office_user_seats}
                    onChange={(v) => set('office_user_seats', v)}
                    placeholder="Unlimited if blank"
                  />
                </Field>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_welcome_email}
                onChange={(e) => set('send_welcome_email', e.target.checked)}
                className="accent-[#FFD108]"
                data-testid="create-send-welcome"
              />
              Send welcome email with credentials
            </label>

            {/* Allocate optional section */}
            {form.plan === 'professional' && (
              <div className="border border-white/5 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowAllocate((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-[#0e0e0e] hover:bg-[#161616]"
                  data-testid="create-toggle-allocate"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Database className="w-4 h-4 text-[#FFD108]" />
                    EQUIPR Allocate SQL setup
                    <span className="text-[10px] text-muted-foreground font-normal ml-1">optional</span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {showAllocate ? 'Hide' : 'Configure'}
                  </span>
                </button>
                {showAllocate && (
                  <div className="p-4 space-y-3 border-t border-white/5">
                    <p className="text-[11px] text-muted-foreground">
                      Pre-populate the customer's Allocate SQL connection. Leave the
                      username/password blank if you only want to set server + database
                      defaults — the customer will fill the rest in on first import.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="SQL Server host" testid="create-sql-server">
                        <Input
                          value={form.allocate_sql_default_server}
                          onChange={(v) => set('allocate_sql_default_server', v)}
                          placeholder="sql-allocate.equipr.internal"
                          mono
                        />
                      </Field>
                      <Field label="Port" testid="create-sql-port">
                        <Input
                          type="number"
                          value={form.allocate_sql_port}
                          onChange={(v) => set('allocate_sql_port', v)}
                          mono
                        />
                      </Field>
                      <Field label="Database" testid="create-sql-database">
                        <Input
                          value={form.allocate_sql_default_database}
                          onChange={(v) => set('allocate_sql_default_database', v)}
                          placeholder="AllocateDb"
                          mono
                        />
                      </Field>
                      <Field label="SQL username" testid="create-sql-username">
                        <Input
                          value={form.allocate_sql_username}
                          onChange={(v) => set('allocate_sql_username', v)}
                          placeholder="guardian_read"
                          mono
                        />
                      </Field>
                      <Field label="SQL password" testid="create-sql-password">
                        <Input
                          type="password"
                          value={form.allocate_sql_password}
                          onChange={(v) => set('allocate_sql_password', v)}
                          placeholder=""
                          mono
                        />
                      </Field>
                    </div>
                    <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-[#0e0e0e] rounded-md p-2 border border-white/5">
                      <KeyRound className="w-3.5 h-3.5 text-[#FFD108] flex-shrink-0 mt-0.5" />
                      <span>
                        Passwords are encrypted at rest. Use a read-only SQL user with
                        EXEC permission on <code className="font-mono">sp_Guardian_ExportAssets</code>.
                      </span>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        checked={form.enable_allocate_integration}
                        onChange={(e) => set('enable_allocate_integration', e.target.checked)}
                        className="accent-[#FFD108]"
                        data-testid="create-enable-allocate"
                      />
                      Enable the Allocate integration toggle for this tenant
                    </label>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="h-10 px-4 rounded-lg text-sm text-muted-foreground hover:text-white"
                data-testid="admin-create-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="h-10 px-5 rounded-lg bg-[#FFD108] text-black text-sm font-semibold uppercase tracking-wider hover:bg-[#ffdf3a] disabled:opacity-60 flex items-center gap-2"
                data-testid="admin-create-submit"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? 'Provisioning…' : 'Create tenant'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ─────────────────── helpers ───────────────────

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
      {label}
    </label>
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder = '', type = 'text', mono = false, autoFocus = false }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    autoFocus={autoFocus}
    className={`w-full h-10 px-3 rounded-lg bg-[#0e0e0e] border border-white/5 text-sm text-white focus:outline-none focus:border-[#FFD108]/40 ${
      mono ? 'font-mono' : ''
    }`}
  />
);

const SuccessCard = ({ result, copy, onClose }) => {
  const fullCredsBlob = `Client ID: ${result.client_id}\nEmail: ${result.admin.email}\nTemporary password: ${result.admin.initial_password}`;
  return (
    <div className="space-y-4" data-testid="admin-create-success">
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-3 flex items-center gap-2 text-sm text-emerald-400">
        <CheckCircle2 className="w-4 h-4" />
        Tenant provisioned · {result.welcome_email_sent && 'welcome email sent'}
      </div>

      <div className="rounded-lg bg-[#0e0e0e] border border-white/5 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Credentials</span>
          <button
            onClick={() => copy('Credentials', fullCredsBlob)}
            className="text-[10px] uppercase tracking-widest text-[#FFD108] hover:text-[#ffdf3a] flex items-center gap-1"
            data-testid="admin-create-copy-all"
          >
            <Copy className="w-3 h-3" /> Copy all
          </button>
        </div>
        <Row label="Client ID" value={result.client_id} copy={copy} mono />
        <Row label="Login email" value={result.admin.email} copy={copy} />
        <Row label="Temporary password" value={result.admin.initial_password} copy={copy} mono highlight />
      </div>

      {result.allocate.integration_enabled && (
        <div className="rounded-lg border border-[#FFD108]/30 bg-[#FFD108]/[0.04] p-3 text-xs flex items-start gap-2">
          <Database className="w-4 h-4 text-[#FFD108] flex-shrink-0 mt-0.5" />
          <div>
            <strong className="text-[#FFD108]">Allocate integration enabled.</strong>{' '}
            {result.allocate.credentials_saved
              ? 'SQL credentials saved — the customer can run their first import immediately.'
              : result.allocate.defaults_set
                ? 'Server + database pre-set; customer enters SQL user/password on first import.'
                : 'Customer can connect their SQL Server from Settings → Integrations.'}
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground italic">
        This password is shown ONCE — copy it now. We've also emailed it to the admin
        if "send welcome email" was on. They'll be prompted to change it from
        Settings → Profile.
      </p>

      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="h-10 px-5 rounded-lg bg-[#FFD108] text-black text-sm font-semibold uppercase tracking-wider hover:bg-[#ffdf3a]"
          data-testid="admin-create-done"
        >
          Done
        </button>
      </div>
    </div>
  );
};

const Row = ({ label, value, copy, mono = false, highlight = false }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className="flex items-center gap-2">
      <span
        className={`text-sm ${mono ? 'font-mono' : ''} ${highlight ? 'text-[#FFD108] font-semibold' : 'text-white'}`}
      >
        {value}
      </span>
      <button
        onClick={() => copy(label, value)}
        className="text-muted-foreground hover:text-[#FFD108] p-1 rounded"
        title={`Copy ${label}`}
      >
        <Copy className="w-3 h-3" />
      </button>
    </div>
  </div>
);

export default AdminCreateTenantModal;
